"use client";

import { useId, useState } from "react";

import { HudWindow } from "@/components/ownly/HudWindow";
import {
  CV_LABEL,
  LINKEDIN_LABEL,
  MIN_MESSAGE_CHARS,
  messageError,
  normaliseUrl,
  proofLinksError,
} from "@/lib/careers-links";
import { CATEGORY_BLURBS } from "@/lib/lead-engine-jobs";
import {
  TurnstileCheck,
  TurnstileScript,
  turnstileNotReadyMessage,
  useTurnstile,
} from "@/components/ownly/TurnstileWidget";

const inputClass =
  "w-full rounded-2xl border border-white/[0.14] bg-ownly-surface/90 px-4 py-3 text-copy text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] outline-none backdrop-blur-md transition-[border-color,box-shadow] placeholder:text-white/60 focus:border-ownly-accent/55 focus:ring-2 focus:ring-ownly-accent/22";
const labelClass =
  "mb-1.5 block text-sm font-medium uppercase tracking-[0.12em] text-white/55";
const helperClass = "mt-1.5 text-sm text-white/60";

type Role = "sales" | "software_dev" | "design" | "marketing" | "ops" | "other";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roleApplied: Role;
  linkedinUrl: string;
  cvUrl: string;
  message: string;
};

const INITIAL_STATE: FormState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  roleApplied: "sales",
  linkedinUrl: "",
  cvUrl: "",
  message: "",
};

const ROLE_LABEL: Record<Role, string> = {
  sales: "Sales",
  software_dev: "Software development",
  design: "Design",
  marketing: "Marketing",
  ops: "Operations / admin",
  other: "Something else",
};

export type CareersApplyFormProps = {
  /** Optional Cloudflare Turnstile site key. When omitted the widget is skipped (dev). */
  turnstileSiteKey?: string;
  /** Optional pre-selected role (e.g. when arriving from a role-specific CTA). */
  initialRole?: Role;
  /** Categories that aren't currently accepting applications — rendered as disabled options in
   *  the dropdown so candidates can still see them but can't submit. The intake on the lead-engine
   *  side enforces this server-side too, so a stale page can't sneak a submission through. */
  closedCategories?: Role[];
  /** When all categories are closed the form is disabled entirely. */
  allClosed?: boolean;
};

export function CareersApplyForm({
  initialRole,
  closedCategories,
  allClosed,
}: CareersApplyFormProps) {
  // The prop is kept so the page's API is unchanged, but the shared hook reads the same
  // public key itself, which is what every other form does.
  const turnstile = useTurnstile();
  const widgetContainerId = useId();
  const closedSet = new Set<Role>(closedCategories ?? []);
  // Pick a sensible default that isn't closed — prefer the explicit initialRole, otherwise the
  // first open category. Falls back to "sales" if literally everything is closed (form is
  // disabled in that case anyway).
  const defaultRole: Role =
    initialRole && !closedSet.has(initialRole)
      ? initialRole
      : ((Object.keys(ROLE_LABEL) as Role[]).find((r) => !closedSet.has(r)) ??
        "sales");
  const [form, setForm] = useState<FormState>(() => ({
    ...INITIAL_STATE,
    roleApplied: defaultRole,
  }));
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Honeypot: bots fill this; humans never see it (field is visually hidden below).
  const [honeypot, setHoneypot] = useState("");

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;

    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError("Please add your first and last name.");
      return;
    }
    if (!form.email.trim() || !form.email.includes("@")) {
      setError("Please add a valid email address.");
      return;
    }
    const linksError = proofLinksError(form.linkedinUrl, form.cvUrl);
    if (linksError) {
      setError(linksError);
      return;
    }
    const aboutError = messageError(form.message);
    if (aboutError) {
      setError(aboutError);
      return;
    }
    if (!turnstile.ready) {
      setError(turnstileNotReadyMessage(turnstile.status));
      return;
    }

    try {
      setPending(true);
      setError(null);

      // Both are required (checked above). Send the normalised form so "linkedin.com/in/x"
      // arrives as a clickable https link in lead-engine.
      const links = [
        {
          label: LINKEDIN_LABEL,
          url: normaliseUrl(form.linkedinUrl)?.toString() ?? form.linkedinUrl.trim(),
        },
        {
          label: CV_LABEL,
          url: normaliseUrl(form.cvUrl)?.toString() ?? form.cvUrl.trim(),
        },
      ];

      let response: Response;
      try {
        response = await fetch("/api/careers/apply", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim() || undefined,
            roleApplied: form.roleApplied,
            source: "ownlydigital.co.uk/careers",
            message: form.message.trim(),
            links,
            turnstileToken: turnstile.token,
            website: honeypot,
          }),
        });
      } catch {
        // The request never reached the server (offline, DNS, timeout). Never surface the raw
        // browser error ("Failed to fetch" and similar) to the applicant.
        throw new Error(
          "Couldn't reach the server. Check your connection and try again.",
        );
      }

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        // Every message /api/careers/apply returns is already written for a human: a rate
        // limit, a role that closed between page load and submit, a failed security check.
        // Show it as-is. Previously only messages containing "security" were shown and
        // everything else, including "that area just stopped taking applications, pick
        // another", was replaced with a generic "please try again" that would just fail the
        // same way again, since the actual fix (pick a different area) was never surfaced.
        throw new Error(
          data.error ||
            "Couldn't submit your application. Please try again, or email team@ownlydigital.co.uk.",
        );
      }

      setSubmitted(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again, or email team@ownlydigital.co.uk.",
      );
      turnstile.reset();
    } finally {
      setPending(false);
    }
  }

  if (submitted) {
    return (
      <HudWindow title="application received">
        <div className="px-5 py-8 text-white sm:px-8 sm:py-10">
          <p className="text-sm font-semibold tracking-[0.01em] text-ownly-accent-text">
            Status · received
          </p>
          <h2 className="ownly-display ownly-section-title mt-3">
            Thanks. We&apos;ve got it.
          </h2>
          <p className="ownly-body mt-4 leading-[27px] text-white/85">
            Your application is in front of the team. If it looks like a fit
            we&apos;ll be in touch from{" "}
            <span className="text-white">team@ownlydigital.co.uk</span> within a
            few working days. If you don&apos;t hear from us, it&apos;s not the
            right fit this time. No hard feelings.
          </p>
        </div>
      </HudWindow>
    );
  }

  return (
    <>
      <TurnstileScript />
      <HudWindow
        title="ownly · application"
        titleBarExtra={
          <span className="rounded border border-ownly-accent/45 bg-gradient-to-r from-ownly-accent/20 to-[#7c5cff]/20 px-2 py-0.5 ownly-label text-ownly-accent-text shadow-[0_0_24px_-10px_rgba(43,127,255,0.85)]">
            APPLY
          </span>
        }
      >
        <form
          onSubmit={handleSubmit}
          className="grid gap-5 px-5 py-7 sm:px-8 sm:py-9"
          noValidate
          autoComplete="on"
        >
          {allClosed ? (
            <p className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm leading-[22px] text-white/70">
              We are not taking applicants at the moment, so this form is
              closed. Nothing sent from here will reach us until an area
              reopens.
            </p>
          ) : null}

          {/* Every control sits inside one fieldset so pausing recruitment disables the whole form
              natively (and drops it out of the tab order) rather than leaving people to fill in
              eight fields before finding out the submit button is dead. */}
          <fieldset
            disabled={allClosed}
            className={`grid min-w-0 gap-5 border-0 p-0 ${allClosed ? "opacity-55" : ""}`}
          >
            {/* Honeypot: bots fill this; humans never see it. */}
            <div
              className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
              aria-hidden
            >
              <label htmlFor={`${widgetContainerId}-website`}>Website</label>
              <input
                id={`${widgetContainerId}-website`}
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-firstName`}
                >
                  First name
                </label>
                <input
                  id={`${widgetContainerId}-firstName`}
                  name="firstName"
                  type="text"
                  required
                  maxLength={80}
                  value={form.firstName}
                  onChange={(e) => update("firstName", e.target.value)}
                  autoComplete="given-name"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-lastName`}
                >
                  Last name
                </label>
                <input
                  id={`${widgetContainerId}-lastName`}
                  name="lastName"
                  type="text"
                  required
                  maxLength={80}
                  value={form.lastName}
                  onChange={(e) => update("lastName", e.target.value)}
                  autoComplete="family-name"
                  className={inputClass}
                />
              </div>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-email`}
                >
                  Email
                </label>
                <input
                  id={`${widgetContainerId}-email`}
                  name="email"
                  type="email"
                  required
                  maxLength={254}
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  autoComplete="email"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-phone`}
                >
                  Phone (optional)
                </label>
                <input
                  id={`${widgetContainerId}-phone`}
                  name="phone"
                  type="tel"
                  maxLength={40}
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  autoComplete="tel"
                  className={inputClass}
                />
              </div>
            </div>

            {/* Radio cards rather than a <select>: a dropdown can only show the role's name, and
                applicants kept having to guess what "Operations / admin" or "Other" meant. Each
                card carries a one or two line description of the actual job underneath. Native
                radios keep arrow-key navigation and screen reader grouping for free. */}
            <fieldset className="min-w-0 border-0 p-0">
              <legend className={labelClass}>
                Which area are you applying for?
              </legend>
              <div className="mt-1 grid gap-3 sm:grid-cols-2">
                {(Object.keys(ROLE_LABEL) as Role[]).map((r) => {
                  const isClosed = closedSet.has(r);
                  const inputId = `${widgetContainerId}-role-${r}`;
                  return (
                    <label
                      key={r}
                      htmlFor={inputId}
                      className={`group relative flex gap-3.5 rounded-2xl border px-4 py-4 transition-[border-color,background-color,box-shadow] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ownly-accent/60 ${
                        isClosed
                          ? "cursor-not-allowed border-white/[0.06] bg-white/[0.015] opacity-55"
                          : "cursor-pointer border-white/[0.12] bg-ownly-surface/70 hover:border-white/25 has-[:checked]:border-ownly-accent/70 has-[:checked]:bg-ownly-accent/[0.09] has-[:checked]:shadow-[0_0_0_1px_rgba(43,127,255,0.35),0_18px_40px_-24px_rgba(43,127,255,0.9)]"
                      }`}
                    >
                      <input
                        id={inputId}
                        type="radio"
                        name="roleApplied"
                        value={r}
                        checked={form.roleApplied === r}
                        disabled={isClosed || allClosed}
                        onChange={() => {
                          if (!closedSet.has(r)) update("roleApplied", r);
                        }}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden
                        className="mt-[3px] grid size-[18px] shrink-0 place-items-center rounded-full border border-white/35 transition-colors peer-checked:border-ownly-accent peer-checked:bg-ownly-accent/15 [&>span]:scale-0 peer-checked:[&>span]:scale-100"
                      >
                        <span className="size-2 rounded-full bg-ownly-accent-text transition-transform" />
                      </span>
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                          <span className="text-copy font-semibold text-white">
                            {ROLE_LABEL[r]}
                          </span>
                          {isClosed ? (
                            <span className="text-xs font-medium uppercase tracking-[0.12em] text-white/55">
                              Not taking applicants
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-1 block text-sm leading-[21px] text-white/68">
                          {CATEGORY_BLURBS[r]}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className={helperClass}>
                All engagements are on a subcontractor basis. Tell us what you
                do in the message below.
              </p>
            </fieldset>

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-linkedin`}
                >
                  LinkedIn profile
                </label>
                <input
                  id={`${widgetContainerId}-linkedin`}
                  name="linkedinUrl"
                  type="url"
                  required
                  aria-required="true"
                  maxLength={500}
                  value={form.linkedinUrl}
                  onChange={(e) => update("linkedinUrl", e.target.value)}
                  placeholder="https://www.linkedin.com/in/…"
                  className={inputClass}
                />
              </div>
              <div>
                <label
                  className={labelClass}
                  htmlFor={`${widgetContainerId}-cv`}
                >
                  CV or portfolio link
                </label>
                <input
                  id={`${widgetContainerId}-cv`}
                  name="cvUrl"
                  type="url"
                  required
                  aria-required="true"
                  maxLength={500}
                  value={form.cvUrl}
                  onChange={(e) => update("cvUrl", e.target.value)}
                  placeholder="Google Drive, Dropbox or your own site"
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label
                className={labelClass}
                htmlFor={`${widgetContainerId}-message`}
              >
                Tell us about you
              </label>
              <textarea
                id={`${widgetContainerId}-message`}
                name="message"
                required
                aria-required="true"
                minLength={MIN_MESSAGE_CHARS}
                rows={5}
                maxLength={4000}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="A couple of paragraphs is plenty. What you've done, what you'd want from this role, real numbers where you can."
                className={inputClass}
              />
            </div>

            <TurnstileCheck turnstile={turnstile} />

            {error ? (
              <p
                className="rounded-2xl border border-red-400/40 bg-red-400/[0.08] px-4 py-3 text-sm text-red-100/95"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-white/60">
                We&apos;ll only use what you submit to evaluate your
                application. See our{" "}
                <a
                  href="/privacy"
                  className="underline decoration-white/25 underline-offset-2 transition hover:text-white/70"
                >
                  privacy policy
                </a>{" "}
                for how we handle it and how long we keep it.
              </p>
              <button
                type="submit"
                disabled={pending || allClosed || !turnstile.ready}
                // Paused recruitment drops the accent fill: a bright blue CTA reading "not taking
                // applications" looks like a live button people will keep clicking.
                //
                // `ownly-btn-primary` is here for the accessible fill, not the shine: this was
                // the last button on the site still painting white text straight onto
                // --ownly-accent, which measures 3.66:1 and fails AA. It was missed because it
                // is the one primary button written by hand rather than from the shared class.
                className={`inline-flex h-12 items-center justify-center rounded-full px-6 text-copy font-semibold tracking-tight transition-[transform,background-color] active:scale-[0.99] disabled:cursor-not-allowed ${
                  allClosed
                    ? "border border-white/12 bg-white/[0.05] text-white/55"
                    : "ownly-btn-primary bg-ownly-accent text-white shadow-[0_18px_36px_-18px_rgba(43,127,255,0.85)] disabled:opacity-60"
                }`}
              >
                {allClosed
                  ? "Not taking applications right now"
                  : pending
                    ? "Sending…"
                    : "Submit application"}
              </button>
            </div>
          </fieldset>
        </form>
      </HudWindow>
    </>
  );
}
